import { useMemo } from 'react'

import { VenueMapTable } from './venue-map-table'
import { createListLookup, createLookup } from '@utils/lookup'
import { VenueLayout, VenueLayoutSection, VenueLayoutTable } from 'types/model/venue-layout'
import { InventoryItem, Venue } from 'types/model'

export interface VenueMapLayerProps {
  venue: Venue
  layout: VenueLayout
  sections: VenueLayoutSection[]
  selectedSection?: VenueLayoutSection
  onSectionSelect?: (sel?: VenueLayoutSection) => void
  tables: VenueLayoutTable[]
  sectionInventory?: Record<string, InventoryItem[]>
}

export function VenueMapLayer({
  venue,
  layout,
  sections,
  selectedSection,
  onSectionSelect,
  tables,
  sectionInventory,
}: VenueMapLayerProps) {
  const sectionsById = useMemo(
    () => sections && createLookup({ of: sections, by: 'id' }),
    [sections]
  )

  const sectionMinPrices = useMemo(() => {
    return (
      sectionInventory &&
      Object.keys(sectionInventory).reduce((acc, cur) => {
        const prices = sectionInventory[cur].map((inv) => inv.price)
        acc[cur] = Math.min(...prices)
        return acc
      }, {} as Record<string, number>)
    )
  }, [sectionInventory])

  const sectionQuantities: Record<string, number> = useMemo(() => {
    if (!sectionInventory) return {}
    return Object.keys(sectionInventory).reduce((acc, cur) => {
      const quantities = sectionInventory[cur].map((inv) => inv.quantityLeft ?? 0)
      const sectionQuantity = quantities.reduce((sum, qty) => sum + qty)
      acc[cur] = sectionQuantity
      return acc
    }, {} as Record<string, number>)
  }, [sectionInventory])

  const tablesBySection = useMemo(
    () => createListLookup({ of: tables, by: 'venueLayoutSection' }),
    [tables]
  )

  return (
    <div className="relative w-full">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={layout.floorPlan.small}
        alt={layout.name}
        loading="eager"
        className="w-full h-auto"
      />
      {sections.map((section) => (
        <div key={section.id} className="absolute w-full group contents">
          {tablesBySection[section.id]?.map(
            (table) =>
              !table.deleted &&
              !!(table.enable ?? true) && (
                <VenueMapTable
                  key={table.id}
                  venue={venue}
                  table={table}
                  section={
                    table.venueLayoutSection ? sectionsById[table.venueLayoutSection] : undefined
                  }
                  soldOut={sectionQuantities[section.id] <= 0}
                  selectedSection={selectedSection}
                  onSectionSelect={onSectionSelect}
                  sectionMinPrice={
                    table.venueLayoutSection
                      ? sectionMinPrices?.[table.venueLayoutSection]
                      : undefined
                  }
                />
              )
          )}
        </div>
      ))}
    </div>
  )
}
